import { DateTime } from "luxon";

import { getDefaultEndTime, getDefaultStartTime } from "~components/RequestForm/hooks";
import { tzOffsetInMinutes } from "~utils";

import { PositionModel, type PositionResponse } from "./PositionModel";
import { SchoolHoursModel } from "./SchoolHoursModel";

const SCHOOL_CANCEL_POLICY_DEFAULT = 24;

type SharedPolicies = {
  leavingClassroom?: string;
  lunchProtocol?: string;
  dressCode?: string;
  electronics?: string;
};

type RateItem = {
  halfDay: { amount: string };
  halfDayLongTerm: { amount: string };
  fullDay: { amount: string };
  fullDayLongTerm: { amount: string };
};

export type RateObjectType = {
  positionName: string;
  rates: RateItem;
};

export type CredentialItem = {
  keyword: string;
  name: string;
};

export type StudentPolicies = SharedPolicies;

export type SubPolicies = SharedPolicies & {
  emergencyProtocol?: string;
  lockdownProtocol?: string;
  escalation?: string;
  takingAttendance?: string;
  codeWords?: string;
};

export type SchoolYearType = {
  id?: string;
  start: string;
  end: string;
};

export const BUDGET_TYPES = {
  SCHOOL: "SCHOOL",
  BUDGET: "BUDGET",
  DISTRICT: "DISTRICT",
} as const;

export const DEFAULT_REQUEST_CRED_SETTINGS = {
  CREDENTIALED: "CREDENTIALED",
  NONCREDENTIALED: "NONCREDENTIALED",
  USE_FORM_VALUE: "USE_FORM_VALUE",
} as const;

export type SchoolClosure = {
  id: string;
  start: string;
  end: string;
  reason: string;
};

export class SchoolModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private school: any;
  private _schoolHours: SchoolHoursModel;
  private _positions: PositionModel[] | undefined;
  private _timezoneIANA: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(school: any) {
    this.school = school;
    this._schoolHours = new SchoolHoursModel(school?.["school/hours"]);
    this._timezoneIANA = school?.["school/time-zone"];
    this._positions = school?.["school/positions"]
      ?.map((array: PositionResponse) => {
        return new PositionModel(array);
      })
      ?.sort((a: { displayName: string }, b: { displayName: string }) =>
        a.displayName.localeCompare(b.displayName),
      );
  }

  get name() {
    return this.school?.["school/name"];
  }

  get raw() {
    return this.school;
  }

  get summary() {
    return this.school?.summary;
  }

  get websiteUrl() {
    return this.school?.websiteUrl;
  }

  get isInternalCodeRequired() {
    return this.school?.requireInternalCode;
  }

  get id() {
    return this.school?.["school/id"] as string;
  }

  get hours() {
    return this._schoolHours;
  }

  get positions() {
    return this._positions;
  }

  get streetAddress() {
    return this.school?.["school/address"]?.street;
  }

  get city() {
    return this.school?.["school/address"]?.city;
  }

  get state() {
    return this.school?.["school/address"]?.state;
  }

  get zip() {
    return this.school?.["school/address"]?.zip;
  }

  get districtId() {
    return this.school?.["school/district"]["district/id"];
  }

  get cancelPolicy() {
    return this.school?.["school/cancel-policy"] || SCHOOL_CANCEL_POLICY_DEFAULT;
  }

  get teachers() {
    return this.school?.["teachers"] || [];
  }

  get defaultNotes() {
    return this.school?.["school/default-notes"];
  }

  get defaultGrades(): string[] {
    return this.school?.["school/default-grades"] || [];
  }

  get tzString() {
    return this._timezoneIANA;
  }

  get timeZone() {
    return tzOffsetInMinutes(this._timezoneIANA);
  }

  get acceptedCredentials() {
    return this.school?.acceptedCredentials;
  }

  get closures(): SchoolClosure[] {
    return this.school?.closures || [];
  }

  get dayOfContact() {
    return this.school?.dayOfContact;
  }

  get dayOfPhone() {
    return this.school?.dayOfPhone;
  }

  get dayOfEmail() {
    return this.school?.dayOfEmail;
  }

  get admins() {
    return this.school?.admins || [];
  }

  get parkingCost() {
    return this.school?.parkingCost;
  }

  get parkingLocation() {
    return this.school?.parkingLocation;
  }

  get checkInLocation() {
    return this.school?.checkinLocation;
  }

  get checkInProcedure() {
    return this.school?.checkinProcedure;
  }

  get checkoutLocation() {
    return this.school?.checkoutLocation;
  }

  get checkoutProcedure() {
    return this.school?.checkoutProcedure;
  }

  get studentPolicies(): StudentPolicies {
    return this.school?.studentPolicies;
  }

  get subPolicies(): SubPolicies {
    return this.school?.subPolicies;
  }

  get payRates(): RateObjectType[] {
    return this.school?.payRates;
  }

  get credentialTypes(): CredentialItem[] {
    return this.school?.acceptedCredentials;
  }

  get hideCredentialToggle(): boolean {
    return this.school?.hideCredentialToggle;
  }

  get years(): SchoolYearType[] | undefined {
    return this.school?.years || [];
  }

  get defaultRequestCredentialSetting(): keyof typeof DEFAULT_REQUEST_CRED_SETTINGS {
    return this.school?.defaultRequestCredentialSetting;
  }

  getPosition(positionId: string | undefined) {
    return this.positions?.find((selectedPosition: PositionModel) => {
      return selectedPosition.id === positionId;
    });
  }

  static getDefaultPosition(positions: PositionModel[] | undefined) {
    return (
      positions?.find((selectedPosition: PositionModel) => {
        return (
          selectedPosition.displayName?.toLowerCase() === "certificated teacher" ||
          selectedPosition.displayName?.toLowerCase() === "teacher"
        );
      }) || positions?.at(0) // This scenario is if somehow the BE is missing the Certificated Teacher position or the Teacher position
    );
  }
}

/***** Factories *****/
export function newSchoolClosure(params: Partial<SchoolClosure>) {
  let { start, end } = params;

  // If just the start date is provided, set the end date to the end of the
  // school day
  if (start && !end) {
    end = getDefaultEndTime(DateTime.fromISO(start)).toISO();
  }
  // If just the end date is given, set the start date to be the start of the
  // school day
  else if (end && !start) {
    start = getDefaultStartTime(DateTime.fromISO(end)).toISO();
  }
  // If both the start and end date are not provided, default to today's date
  else if (!start && !end) {
    start = getDefaultStartTime(DateTime.now()).toISO();
    end = getDefaultEndTime(DateTime.now()).toISO();
  }

  return {
    id: params.id || "1",
    start: start || "",
    end: end || "",
    reason: params.reason || "Non-School Day",
  } satisfies SchoolClosure;
}
